import { View } from "react-native";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "lodash";
import SettingsItem from "../common/settings-item";
import { useSettingsData } from "../../../../contexts/SettingsDataContext";
import { MAX_GREETING_LENGTH } from "./greeting-constants";
import StyledText from "../../../common/ui/styled-text";
import { Input } from "../../../common/ui/input.native";

const DEBOUNCE_AMOUNT_MS = 800;

const GreetingInput = () => {
  const {
    settings,
    handleSaveGreetingMessageOpen,
    handleSaveGreetingMessageClosed,
    validationErrors,
    isTestCallActive,
  } = useSettingsData();

  const [localGreetingMessageOpen, setLocalGreetingMessageOpen] = useState(
    settings.greetingMessageOpen || ""
  );
  const [localGreetingMessageClosed, setLocalGreetingMessageClosed] = useState(
    settings.greetingMessageClosed || ""
  );

  useEffect(() => {
    setLocalGreetingMessageOpen(settings.greetingMessageOpen || "");
    setLocalGreetingMessageClosed(settings.greetingMessageClosed || "");
  }, [settings.greetingMessageOpen, settings.greetingMessageClosed]);

  const debouncedGreetingMessageOpen = useMemo(
    () => debounce(handleSaveGreetingMessageOpen, DEBOUNCE_AMOUNT_MS),
    [handleSaveGreetingMessageOpen]
  );
  const debouncedGreetingMessageClosed = useMemo(
    () => debounce(handleSaveGreetingMessageClosed, DEBOUNCE_AMOUNT_MS),
    [handleSaveGreetingMessageClosed]
  );

  useEffect(() => {
    return () => {
      debouncedGreetingMessageOpen.cancel();
      debouncedGreetingMessageClosed.cancel();
    };
  }, [debouncedGreetingMessageOpen, debouncedGreetingMessageClosed]);

  const handleGreetingMessageOpenChange = useCallback(
    (text: string) => {
      setLocalGreetingMessageOpen(text);
      debouncedGreetingMessageOpen(text);
    },
    [debouncedGreetingMessageOpen, setLocalGreetingMessageOpen]
  );

  const handleGreetingMessageClosedChange = useCallback(
    (text: string) => {
      setLocalGreetingMessageClosed(text);
      debouncedGreetingMessageClosed(text);
    },
    [debouncedGreetingMessageClosed, setLocalGreetingMessageClosed]
  );

  return (
    <View>
      <View className="mb-7">
        <SettingsItem
          title="Open Hours Greeting"
          description="Initial greeting for calls during business hours."
          required={true}
        >
          {validationErrors.greetingMessageOpen && (
            <StyledText className="text-xs text-red-500 mb-1">
              {validationErrors.greetingMessageOpen}
            </StyledText>
          )}
          <View
            className={`border border-gray-300 rounded-md ${
              validationErrors.greetingMessageOpen ? "border-red-500" : ""
            }`}
          >
            <Input
              className="p-3 text-gray-800 text-base"
              placeholder="Ex. Hi this is Loman's Pizza, how may I help you?"
              value={localGreetingMessageOpen || ""}
              onChangeText={handleGreetingMessageOpenChange}
              multiline={true}
              numberOfLines={3}
              maxLength={MAX_GREETING_LENGTH}
              editable={!isTestCallActive}
            />
          </View>
          <StyledText className="text-xs text-gray-500 mt-2 text-right">
            {localGreetingMessageOpen.length}/{MAX_GREETING_LENGTH} characters
          </StyledText>
        </SettingsItem>
      </View>
      <View>
        <SettingsItem
          title="Closed Hours Greeting"
          descriptionComponent={
            <StyledText className="text-sm text-gray-500">
              Initial greeting for calls outside of business hours.{" "}
              <span className="font-medium text-gray-700">
                Your greeting should clearly state that the business is
                currently closed.
              </span>
            </StyledText>
          }
          required={true}
        >
          {validationErrors.greetingMessageClosed && (
            <StyledText className="text-xs text-red-500 mb-1">
              {validationErrors.greetingMessageClosed}
            </StyledText>
          )}
          <View
            className={`border border-gray-300 rounded-md ${
              validationErrors.greetingMessageClosed ? "border-red-500" : ""
            }`}
          >
            <Input
              className="p-3 text-gray-800 text-base"
              placeholder="Ex. Thanks for calling Loman's Pizza. Please note that our kitchen is currently closed. How can I help you today?"
              value={localGreetingMessageClosed || ""}
              onChangeText={handleGreetingMessageClosedChange}
              multiline={true}
              numberOfLines={3}
              maxLength={MAX_GREETING_LENGTH}
              editable={!isTestCallActive}
            />
          </View>
          <StyledText className="text-xs text-gray-500 mt-2 text-right">
            {localGreetingMessageClosed.length}/{MAX_GREETING_LENGTH} characters
          </StyledText>
        </SettingsItem>
      </View>
    </View>
  );
};

export default memo(GreetingInput);
